"use client";

import trackClick from "@faire/web-api/api/click-track/post";
import { IClickInfo } from "@faire/web-api/indigofair/data/IClickInfo";
import { logWarning } from "@faire/web/common/logging";
import React, { PropsWithChildren, useCallback, useContext } from "react";

import { ILinkProps, Link } from "./LinkAnchor";

export interface ITrackedLinkContextData {
  type: IClickInfo.Type;
  requestId?: string;
  containerName: string;
}

export const TrackedLinkContext = React.createContext<
  ITrackedLinkContextData | undefined
>(undefined);

export namespace TrackedLink {
  export interface IProps extends ILinkProps {
    token: string;
  }
}

/**
 * Element for quickly tracking that a URL was followed, in association
 * with an entity token.
 * @example ```jsx
 * <TrackedLink
 *   onClick={this.onProductClick}
 *   to={this.props.generateProductUrl(this.props.product)}
 *   token={this.props.product.token!}
 * >
 *   <ProductDetails />
 * </TrackedLink>
 * ```
 */
export const TrackedLink = (props: PropsWithChildren<TrackedLink.IProps>) => {
  const { children, ...linkProps } = props;

  const clickInfo = useContext(TrackedLinkContext);

  const handleClick: ILinkProps["onClick"] = useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>) => {
      if (clickInfo) {
        trackClick(
          IClickInfo.build({
            token: props.token,
            type: clickInfo.type,
            container_name: clickInfo.containerName,
            request_id: clickInfo.requestId || "",
          })
        );
      } else {
        logWarning(
          new Error(
            "TrackedLink component clicked outside of TrackedLinkContext.Provider!"
          )
        );
      }

      if (props.onClick) {
        props.onClick(event);
      }
    },
    [clickInfo, props]
  );

  if (clickInfo === undefined) {
    return <Link {...linkProps}>{children}</Link>;
  }

  return (
    <Link {...linkProps} onClick={handleClick}>
      {children}
    </Link>
  );
};
